import { faCalendarArrowDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ListTaskFragment, useGetTasksQuery } from '../../graphql/schema';
import Loading from '../base/Loading';
import TasksSidebarEntry from './TasksSidebarEntry';

interface Group {
  id: string;
  label: string;
  tasks: ListTaskFragment[];
}

interface Groups {
  today: Group;
  yesterday: Group;
  thisWeek: Group;
  [key: string]: Group;
}

const TasksSidebar = () => {
  const { taskId } = useParams();

  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const { data, loading, fetchMore } = useGetTasksQuery();

  const groupedTasks = useMemo(() => {
    const today = dayjs();
    const yesterday = dayjs().subtract(1, 'day');

    const groups: Groups = {
      today: {
        id: 'today',
        label: 'Heute',
        tasks: [],
      },
      yesterday: {
        id: 'yesterday',
        label: 'Gestern',
        tasks: [],
      },
      thisWeek: {
        id: 'thisWeek',
        label: 'Diese Woche',
        tasks: [],
      },
    };

    data?.tasks.items.forEach((task) => {
      const createdAt = dayjs(task.createdAt);

      if (createdAt.isSame(today, 'day')) {
        groups.today.tasks.push(task);
      } else if (createdAt.isSame(yesterday, 'day')) {
        groups.yesterday.tasks.push(task);
      } else if (createdAt.isSame(today, 'week')) {
        groups.thisWeek.tasks.push(task);
      } else {
        const monthId = createdAt.format('YYYY-MM');
        if (!groups[monthId])
          groups[monthId] = {
            id: createdAt.format('YYYY-MM'),
            label: createdAt.format('MMMM YYYY'),
            tasks: [],
          };
        groups[monthId].tasks.push(task);
      }
    });

    return Object.values(groups);
  }, [data]);

  const handleLoadMore = useCallback(async () => {
    if (!data?.tasks.nextToken) return;

    setLoadingMore(true);
    await fetchMore({
      variables: {
        options: {
          startingToken: data?.tasks.nextToken,
        },
      },
    });
    setLoadingMore(false);
  }, [data, fetchMore]);

  if (loading)
    return (
      <div className="flex flex-col w-[250px]">
        <Loading />
      </div>
    );

  if (data?.tasks.items.length === 0) return null;

  return (
    <div className="flex flex-col p-2">
      {groupedTasks
        .filter((g) => g.tasks.length > 0)
        .map((group) => (
          <div key={group.id} className="space-y-1 mb-4">
            <div className="text-xs font-semibold pl-[6px]">{group.label}</div>
            {group.tasks.map((task) => (
              <TasksSidebarEntry
                task={task}
                key={task.id}
                activeTaskId={taskId}
              />
            ))}
          </div>
        ))}

      {data?.tasks.nextToken && !loadingMore && (
        <div
          onClick={handleLoadMore}
          className="flex justify-center space-x-2 text-sm items-center cursor-pointer mb-3"
        >
          <FontAwesomeIcon icon={faCalendarArrowDown} />
          <span>Ältere anzeigen</span>
        </div>
      )}
      {data?.tasks.nextToken && loadingMore && <Loading />}
    </div>
  );
};

export default TasksSidebar;
